import React from 'react';
import { Link } from 'react-router-dom';
import {
    Shield,
    Cloud,
    Monitor,
    Video,
    CheckCircle2,
    Rocket,
    Settings,
    Lock,
    ArrowRight,
    Server
} from 'lucide-react';
import PageLayout from '../Layout/PageLayout';
import PageHeader from '../PageHeader/PageHeader';
import ModernCTA from '../CTA/ModernCTA';
import './ModernSector.scss';

const ModernDepartment = () => {
    const stats = [
        { number: '5', label: 'Military Branches' },
        { number: '100%', label: 'DoD Compliant' },
        { number: '24/7', label: 'Mission Support' },
        { number: '15+', label: 'Years Experience' }
    ];

    const services = [
        {
            icon: <Shield size={32} />,
            title: 'All Military Branches',
            description: 'Supporting Army, Air Force, Navy, Marines, and Space Force with comprehensive technology solutions.',
            features: ['Army Solutions', 'Navy Operations', 'Air Force Systems', 'Space Force Tech']
        },
        {
            icon: <Settings size={32} />,
            title: 'DoD & 4th Estate Contracts',
            description: 'Expanding access to technical services, software, and support through strategic OEM partnerships.',
            features: ['Contract Vehicles', 'OEM Partnerships', 'Technical Services', 'Software Solutions']
        },
        {
            icon: <Cloud size={32} />,
            title: 'DISA Enterprise Support',
            description: 'Comprehensive support for DISA programs and the entire DISA Enterprise ecosystem.',
            features: ['DISA Programs', 'Enterprise Solutions', 'Network Services', 'Cloud Infrastructure']
        },
        {
            icon: <Rocket size={32} />,
            title: 'Prime Contractor',
            description: 'Leading key DoD contracts including network optimization and modernization efforts.',
            features: ['Prime Contracts', 'Network Optimization', 'Modernization', 'Integration Services']
        }
    ];

    const techOfferings = [
        { name: 'EVOIP Communications', icon: <Video size={24} /> },
        { name: 'Infrastructure Solutions', icon: <Server size={24} /> },
        { name: 'Peripherals & Workstations', icon: <Monitor size={24} /> },
        { name: 'Secure Collaboration', icon: <Lock size={24} /> }
    ];

    const complianceItems = [
        'DoD compliance and security standards',
        'Data protection and encryption',
        'Secure user authentication',
        'Device management and monitoring'
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Department of Defense"
                subtitle="Empower the Warfighter | Execute Innovation | Enable Readiness"
                breadcrumbs={[
                    { label: 'Industries', path: '/serve' },
                    { label: 'Department of Defense' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-sector-hero">
                <div className="jk-sector-hero__container">
                    <div className="jk-sector-hero__grid">
                        <div className="jk-sector-hero__content">
                            <span className="jk-section-label">
                                <Shield size={16} />
                                Defense Technology Solutions
                            </span>
                            <h2 className="jk-sector-hero__title">
                                Serving the
                                <span className="highlight"> Department of Defense</span>
                            </h2>
                            <p className="jk-sector-hero__description">
                                At Jaak Tech, we proudly support evolving DoD missions across all branches
                                and agencies by delivering innovative, secure, and compliant technology
                                solutions. Our expertise ensures warfighter readiness and mission success.
                            </p>
                            <div className="jk-sector-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Learn About Our Services
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/about" className="jk-btn jk-btn--secondary">
                                    About Us
                                </Link>
                            </div>
                        </div>

                        <div className="jk-sector-hero__stats">
                            {stats.map((stat, index) => (
                                <div key={index} className="jk-sector-hero__stat">
                                    <span className="jk-sector-hero__stat-number">{stat.number}</span>
                                    <span className="jk-sector-hero__stat-label">{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="jk-sector-services">
                <div className="jk-sector-services__container">
                    <div className="jk-sector-services__header">
                        <span className="jk-section-label">
                            <Rocket size={16} />
                            Our DoD Services
                        </span>
                        <h2 className="jk-sector-services__title">
                            Mission-Critical
                            <span className="highlight"> Support</span>
                        </h2>
                        <p className="jk-sector-services__subtitle">
                            Innovative, secure, and compliant technology solutions for all DoD branches
                        </p>
                    </div>

                    <div className="jk-sector-services__grid">
                        {services.map((service, index) => (
                            <div key={index} className="jk-sector-services__card">
                                <div className="jk-sector-services__card-icon">
                                    {service.icon}
                                </div>
                                <h3 className="jk-sector-services__card-title">{service.title}</h3>
                                <p className="jk-sector-services__card-description">{service.description}</p>
                                <ul className="jk-sector-services__card-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={14} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technology Offerings Section */}
            <section className="jk-sector-capabilities">
                <div className="jk-sector-capabilities__container">
                    <div className="jk-sector-capabilities__grid">
                        <div className="jk-sector-capabilities__content">
                            <span className="jk-section-label">
                                <Monitor size={16} />
                                Technology Offerings
                            </span>
                            <h2 className="jk-sector-capabilities__title">
                                Our Technology
                                <span className="highlight"> Offerings</span>
                            </h2>
                            <p className="jk-sector-capabilities__text">
                                We provide a comprehensive range of technology solutions designed to meet
                                the unique requirements of Department of Defense operations, ensuring
                                security, reliability, and mission readiness.
                            </p>

                            <div className="jk-sector-capabilities__tech-grid">
                                {techOfferings.map((tech, index) => (
                                    <div key={index} className="jk-sector-capabilities__tech-card">
                                        <div className="jk-sector-capabilities__tech-icon">
                                            {tech.icon}
                                        </div>
                                        <span className="jk-sector-capabilities__tech-name">{tech.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="jk-sector-capabilities__visual">
                            <div className="jk-sector-compliance">
                                <h3 className="jk-sector-compliance__title">Secure & Compliant Solutions</h3>
                                <p className="jk-sector-compliance__text">
                                    Compliant solutions that safeguard users, devices, and data in alignment with DoD regulations
                                </p>
                                <ul className="jk-sector-compliance__list">
                                    {complianceItems.map((item, index) => (
                                        <li key={index}>
                                            <CheckCircle2 size={16} />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <Link to="/contact" className="jk-btn jk-btn--primary" style={{ marginTop: '1.5rem', display: 'inline-flex' }}>
                                Contact Our DoD Team
                                <ArrowRight size={20} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernDepartment;
